import { isEmpty } from 'lodash';
import { catchError, filter, Observable, of, skip, switchMap, take, tap } from 'rxjs';
import { Authorities } from '../../shared/enums/authorities.enum';
import { Languages } from '../../shared/enums/languages.enum';
import { AuthService } from '../services/auth.service';
import { LanguageService } from '../services/language.service';
import { RouteCheckerService } from '../services/route-checker.service';
import { UserConfigurationService } from '../services/user-configuration.service';
import { UserExternalSyncService } from '../services/user-external-sync.service';

export function appInitializer(
  authService: AuthService,
  userConfigurationService: UserConfigurationService,
  languageService: LanguageService,
  userExternalSyncService: UserExternalSyncService,
  routeCheckerService: RouteCheckerService,
): () => Observable<boolean> {
  // If route is unguarded, we don't want to call services that depend on user configuration etc.
  if (routeCheckerService.isPublicRoute()) return () => of(true);

  const token$ = authService.token$.pipe(filter(Boolean), take(1));
  authService.init();

  const fireError = (err: unknown) => {
    const preloaderElement = document.getElementById('preloader-wrapper');
    const mainErrorElement = document.getElementById('main-loading-error');
    const mainErrorRefreshElement = document.getElementById('main-error-refresh');

    if (preloaderElement && mainErrorElement) {
      preloaderElement.style.visibility = 'hidden';
      mainErrorElement.style.visibility = 'visible';
    }

    if (mainErrorRefreshElement) {
      mainErrorRefreshElement.addEventListener('click', () => {
        localStorage.clear();
        sessionStorage.clear();
        document.cookie = '';
        authService.logout();
      });
    }

    throw err;
  };

  const getExternalToken = (token: string): Observable<string> => {
    userExternalSyncService
      .sync(token)
      .pipe(catchError((err) => fireError(err)))
      .subscribe();

    return authService.token$.pipe(skip(1), take(1)); // Skip the old token and take a new one after external sync
  };

  return () => {
    const result = token$.pipe(
      tap(() => authService.getUserProfile()),
      switchMap((token) =>
        authService.user.authorities.includes(Authorities.USER_UPDATE_ADFS) ? getExternalToken(token) : of(token),
      ),
      switchMap((token) => userConfigurationService.loadUserConfiguration(token)),
      switchMap((userConfiguration) =>
        languageService.use(userConfiguration?.languageConfiguration?.language || Languages.DEFAULT),
      ),
      tap((res) => {
        if (!res || isEmpty(res)) {
          fireError('External Integration Failed');
        }
      }),
    );

    return result;
  };
}
